/**
 * src/lib/deviceId.js — which phone is this, across visits?
 *
 * `D-15` (locked 2026-08-21): *"A device id minted on first scan, held in
 * localStorage and a cookie, carried in the booth token, joined to every run,
 * and gate-able from a monitored admin log."* This is that. It was decided,
 * written down, and then not built for ten days, during which `device_id` was
 * NULL on every row ever written (`P11`) and `RATE_LIMIT_PER_HOUR` counted
 * against a `user_id` that regenerated on page load.
 *
 * ⚠️ THIS IS INFRASTRUCTURE, NOT EXPERIENCE, and `D-15` says so explicitly.
 * Brief §4.26 forbids caching *the guest experience* per device — remembered
 * settings, a per-device sound check. An identity the guest never sees is the
 * opposite of that, and D-15 carved it out in writing precisely so a future
 * session would not delete this file citing §4.26. Do not delete this file
 * citing §4.26.
 *
 * ⚠️ THREE STORES, AND THE COOKIE IS THE SERVER'S. We write localStorage and
 * IndexedDB here; `/api/booth-session` sets the cookie with `Set-Cookie`.
 * That split is the whole point:
 *
 *   Safari's ITP caps SCRIPT-WRITTEN storage — localStorage, IndexedDB and
 *   document.cookie alike — at roughly SEVEN DAYS of no interaction. The
 *   festival runs EIGHT WEEKENDS. A guest who came on weekend one and returns
 *   on weekend three is a stranger if JS is the only writer. A cookie set by
 *   the server on a first-party domain is not capped the same way, and
 *   `scenetwist.com` is a real custom domain rather than a *.netlify.app
 *   subdomain — which is what makes it genuinely first-party.
 *
 * ⚠️ IT IS A HINT, NOT A CREDENTIAL. The value is readable and editable by
 * anyone who opens devtools, so the server treats its own cookie as
 * authoritative and this one only as a fallback for a browser that refuses
 * cookies. Nothing may ever be authorised by it. What it buys is that a
 * refresh, a re-scan and a return visit are recognisably the same phone —
 * which is all "first one free" and an honest rate limit ever needed.
 *
 * ⚠️ ANONYMOUS LEAKAGE IS BUDGETED, NOT SOLVED. A private window is a new
 * device. Cleared storage is a new device. That is a fact about the web, and
 * the controls that actually bound the money live in the generator:
 * `event.per_day_spend_usd`, `event.per_hour_runs` and the kill switch.
 */

const KEY = 'st.device';
const DB_NAME = 'st_device';
const STORE = 'kv';

/* A v4 UUID, because it lands in booth_run.user_id via the token's `sub` and
   that column is a uuid. Anything else fails the insert. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let memo = null;

function mint() {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch { /* fall through */ }
  /* Older iOS Safari has crypto.getRandomValues but not randomUUID. */
  const b = new Uint8Array(16);
  globalThis.crypto.getRandomValues(b);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

function readLocal() {
  try {
    const v = localStorage.getItem(KEY);
    return UUID_RE.test(v || '') ? v : null;
  } catch { return null; }
}

function readCookie() {
  try {
    const m = document.cookie.match(/(?:^|;\s*)st_device=([^;]+)/);
    const v = m ? decodeURIComponent(m[1]) : null;
    return UUID_RE.test(v || '') ? v : null;
  } catch { return null; }
}

/* IndexedDB is the slowest of the three and the only async one, so it is a
   BACKFILL rather than part of the read path: nothing waits on it. It exists
   because it survives some clearing that localStorage does not. */
function idb(mode, fn) {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v) => { if (!done) { done = true; resolve(v); } };
    setTimeout(() => finish(null), 400);          // never block a guest on storage
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onerror = () => finish(null);
      req.onsuccess = () => {
        try {
          const tx = req.result.transaction(STORE, mode);
          fn(tx.objectStore(STORE), finish);
          tx.onerror = () => finish(null);
        } catch { finish(null); }
      };
    } catch { finish(null); }
  });
}

const readIdb = () => idb('readonly', (store, finish) => {
  const g = store.get(KEY);
  g.onsuccess = () => finish(UUID_RE.test(g.result || '') ? g.result : null);
  g.onerror = () => finish(null);
});

const writeIdb = (id) => idb('readwrite', (store, finish) => {
  store.put(id, KEY);
  finish(true);
});

/** Write the id back to every store we control. Never throws, never blocks. */
function persist(id) {
  try { localStorage.setItem(KEY, id); } catch { /* private mode */ }
  writeIdb(id);
  /* ⚠️ NOT document.cookie. The server sets it on the booth-session response so
     it is not a script-written cookie — see the ITP note in the header. */
}

/**
 * The id for this device, synchronously, minting one if this is the first
 * visit. Safe to call on every render.
 *
 * ⚠️ COOKIE FIRST. If the server has already given this browser one, that is
 * the value the server will trust, and returning a different one from
 * localStorage would split one phone into two identities.
 */
export function getDeviceId() {
  if (memo) return memo;
  memo = readCookie() || readLocal() || mint();
  persist(memo);
  return memo;
}

/**
 * Recover an id that survived only in IndexedDB, then reconcile the stores.
 * Call once on boot, before the session is minted; it resolves fast or not at
 * all, and a guest never waits on it.
 *
 * ⚠️ AN OLDER ID WINS. If IndexedDB is holding one and we just minted a fresh
 * one this load, the older value is the real device and the new one is an
 * artifact of cleared localStorage. Keeping the new one would silently reset
 * that guest's history — which is the exact failure this file exists to stop.
 */
export async function restoreDeviceId() {
  const current = getDeviceId();
  if (readCookie() || readLocal()) return current;      // already durable
  const older = await readIdb();
  if (older && older !== current) {
    memo = older;
    persist(older);
    return older;
  }
  return current;
}

/**
 * Take the server's answer as final and reconcile our stores to it.
 *
 * ⚠️ THE SERVER'S COOKIE IS THE ONE THAT COUNTS, so if it disagrees with what
 * we sent, we are the ones who are wrong — a browser that cleared localStorage
 * but kept the cookie would otherwise run a second identity alongside the real
 * one, and the guest's history would silently split in two.
 */
export function adoptServerDeviceId(id) {
  if (!UUID_RE.test(id || '') || id === memo) return memo;
  memo = id;
  persist(id);
  return memo;
}
